import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {getAsYouType, parsePhoneNumber} from 'awesome-phonenumber';

import type {GetComponentProps} from '../../utils/utility-types';
import type {TextInput} from '../TextInput';
import type {Props} from './types';

import {getCountries} from '../../utils/countries';
import {logger} from '../../utils/logger';
import {getDeviceCountryCode} from './getDeviceCountryCode';

export const useController = ({
  defaultValue,
  onPhoneChange,
  onSelectCountryClick: onSelectCountryClickProp,
}: Props) => {
  const [formattedNumber, setFormattedNumber] = useState('');

  const deviceCountryCode = useMemo(() => {
    return getDeviceCountryCode();
  }, []);

  const {countryIso2Lookup} = useMemo(() => getCountries(), []);

  const [country, setCountry] = useState(
    countryIso2Lookup[deviceCountryCode] || countryIso2Lookup.us!,
  );
  const [formatter, setFormatter] = useState(getAsYouType(country.iso2));

  const isOnChangeCalled = useRef(false);

  useEffect(() => {
    if (defaultValue) {
      try {
        const pn = parsePhoneNumber(defaultValue);
        const regionCode = pn.regionCode?.toLowerCase() || 'us';

        setCountry(countryIso2Lookup[regionCode] || countryIso2Lookup.us!);
        setFormatter(getAsYouType(regionCode));
        setFormattedNumber(pn.number?.national || '');
      } catch (error) {
        logger.error(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFormatter(getAsYouType(country.iso2));
  }, [country]);

  useEffect(() => {
    if (isOnChangeCalled.current && onPhoneChange) {
      const pn = formatter.getPhoneNumber();

      const phoneNumber =
        pn.number?.e164 || `+${country.dialCode}${pn.number?.input}`;

      onPhoneChange(phoneNumber, pn.valid, !formattedNumber.length);
    }
  }, [formatter, formattedNumber, onPhoneChange, country.dialCode]);

  const onChange: GetComponentProps<typeof TextInput>['onChange'] = useCallback(
    e => {
      isOnChangeCalled.current = true;

      const value = (e.payload?.text || '').replace(/\D+/g, '');

      const fNumber = formatter.reset(value);

      const pn = formatter.getPhoneNumber();

      if (pn.possibility === 'too-long') {
        formatter.removeChar();
      } else {
        setFormattedNumber(fNumber);
      }
    },
    [formatter],
  );

  const setSelectedCountry = useCallback(
    (countryCode: string) => {
      const newCountry =
        countryIso2Lookup[countryCode] || countryIso2Lookup.us!;

      setCountry(newCountry);
      setFormatter(getAsYouType(newCountry.iso2));
      setFormattedNumber('');
    },
    [countryIso2Lookup],
  );

  const onSelectCountryClick = useCallback(() => {
    onSelectCountryClickProp(setSelectedCountry);
  }, [onSelectCountryClickProp, setSelectedCountry]);

  return {country, formattedNumber, onChange, onSelectCountryClick};
};
